/* eslint-disable max-lines */
import { API_TYPES } from "@src/appV2/api";
import {
  INTERVIEW_ATTENDANCE_STATUS,
  INTERVIEW_SCHEDULE_STATUS,
  INTERVIEW_TYPE,
  INTERVIEW_VERIFICATION_STATUS,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { z } from "zod";

import {
  PLACEMENT_STATUS,
  PlacementApplicationMethod,
  PlacementApplicationStatus,
} from "./fetchPlacements.schema";

const placementApplicationPlacementIncludeSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    title: z.string(),
    description: z.string().optional(),
    jobTypes: z.array(z.string()),
    shiftTypes: z.array(z.string()),
    workerTypes: z.array(z.string()),
    payRange: z
      .object({
        minPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
        maxPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
      })
      .optional(),
    address: z
      .object({
        formatted: z.string().optional(),
      })
      .optional(),
    coordinates: z
      .object({
        longitude: z.number(),
        latitude: z.number(),
      })
      .optional(),
    status: z.enum(PLACEMENT_STATUS),
    interviewInstructions: z.string().optional(),
  }),
  meta: z
    .object({
      distance: z.number().optional(),
    })
    .optional(),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementApplicationPlacementInclude = z.infer<
  typeof placementApplicationPlacementIncludeSchema
>;

const placementApplicationWorkplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    type: z.string().optional(),
    state: z.string().optional(),
    photos: z.array(z.string()).optional(),
    tmz: z.string().optional(),
  }),
});
export type PlacementApplicationWorkplaceInclude = z.infer<
  typeof placementApplicationWorkplaceIncludeSchema
>;

const placementApplicationInterviewIncludeSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    start: z.string(),
    end: z.string(),
    createdAt: z.string(),
    status: z.nativeEnum(InterviewStatus),
    type: z.nativeEnum(INTERVIEW_TYPE).optional(),
    scheduleStatus: z.nativeEnum(INTERVIEW_SCHEDULE_STATUS).optional(),
    verificationStatus: z.nativeEnum(INTERVIEW_VERIFICATION_STATUS).optional(),
    workerManualEta: z
      .object({
        etaAt: z.string(),
      })
      .optional(),
    attendanceStatus: z.nativeEnum(INTERVIEW_ATTENDANCE_STATUS).optional(),
  }),
  meta: z
    .object({
      isEtaUpdateEnabled: z.boolean().optional(),
      icsFileUrl: z.string().optional(),
    })
    .optional(),
  relationships: z
    .object({
      placementApplication: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placementApplication),
          id: z.string(),
        }),
      }),
      placementCandidateBan: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.placementCandidateBan),
            id: z.string(),
          }),
        })
        .optional(),
    })
    .optional(),
});
export type PlacementApplicationInterviewInclude = z.infer<
  typeof placementApplicationInterviewIncludeSchema
>;

const placementApplicationPlacementCandidateBanIncludeSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    action: z.string().optional(),
    revokedAt: z.string().optional(),
    startsAt: z.string(),
    expiresAt: z.string().optional(),
    dispute: z
      .object({
        status: z.string(),
        reason: z.string(),
        description: z.string(),
        resolutionReason: z.string().optional(),
      })
      .optional(),
  }),
  meta: z.object({
    isExcused: z.boolean(),
  }),
});
export type PlacementApplicationPlacementCandidateBanInclude = z.infer<
  typeof placementApplicationPlacementCandidateBanIncludeSchema
>;

const placementApplicationsIncludeItemSchema = z.discriminatedUnion("type", [
  placementApplicationPlacementIncludeSchema,
  placementApplicationWorkplaceIncludeSchema,
  placementApplicationInterviewIncludeSchema,
  placementApplicationPlacementCandidateBanIncludeSchema,
]);

export const placementApplicationsIncludeSchema = z.array(placementApplicationsIncludeItemSchema);

export const placementApplicationDataSchema = z.object({
  id: z.string(),
  type: z.literal(API_TYPES.placementApplication),
  attributes: z.object({
    status: z.nativeEnum(PlacementApplicationStatus),
    appliedDate: z.string(),
    updatedAt: z.string(),
    notes: z.string().optional(),
    placementTier: z.string().optional(),
    placementType: z.string().optional(),
    applicationManagementType: z.string().optional(),
    workerApplicationPreferenceDetails: z
      .object({
        yearsOfExperienceByQualification: z.record(z.string(), z.number()),
        notes: z.string(),
        preferredDaysOfWeek: z.array(z.string()),
      })
      .optional(),
  }),
  relationships: z.object({
    placement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placement),
        id: z.string(),
      }),
    }),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    interviews: z.object({
      data: z.array(
        z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        })
      ),
    }),
  }),
  meta: z.object({
    isFavorited: z.boolean(),
    isLicenseEligible: z.boolean(),
    allowRequestInterview: z.boolean(),
    isDocumentRequirementValid: z.boolean(),
    isExemptFromDocumentRequirements: z.boolean(),
    isRecruiterScreeningEnabled: z.boolean(),
    applicationMethod: z.nativeEnum(PlacementApplicationMethod).optional(),
  }),
});
export type PlacementApplicationData = z.infer<typeof placementApplicationDataSchema>;

export const placementApplicationsQuerySchema = z.object({
  filter: z
    .object({
      status: z.nativeEnum(PlacementApplicationStatus).optional(),
      "placement.status": z.string().optional(),
      workplaceIds: z.string().optional(),
      workerTypes: z.string().optional(),
      jobTypes: z.string().optional(),
      shiftTypes: z.string().optional(),
      "interview.start": z
        .object({
          gt: z.string().optional(),
          lt: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  sort: z.enum(["asc", "desc"]).optional(),
  page: z
    .object({
      size: z.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});
export type PlacementApplicationsQuery = z.infer<typeof placementApplicationsQuerySchema>;

export const paginatedPlacementApplicationsResponseSchema = z.object({
  data: z.array(placementApplicationDataSchema),
  included: placementApplicationsIncludeSchema.optional(),
  links: z
    .object({
      next: z.string().optional(),
      prev: z.string().optional(),
    })
    .optional(),
});
export type GetPlacementApplicationsResponse = z.infer<
  typeof paginatedPlacementApplicationsResponseSchema
>;
/* eslint-enable max-lines */
